import { ABTestExperiment } from '@jusbrasil-web/shared-ab-test'
import { GetServerSidePropsContext } from 'next'
import UAParser from 'ua-parser-js'

import { ARTIFACT_LABEL } from '../constants'
import { FeatureFlag } from '../feature-flags'
import { MetricsProperties } from './types'

type Document = {
  artifact: string
  docId: number
  degree?: string | null
  hasDecisionFactsForReference?: boolean | null
  decisionLabel?: string | null
  lawsuitEntity?: {
    url: string | null
  } | null
  decisionTabs?:
    | readonly ({
        label: string | null
        linkId: number | null
      } | null)[]
    | null
}

type Viewer = {
  pid: number
}

type Topic = {
  tid: number
  title: string | null
}

const JURIS_ARTIFACTS = [
  ARTIFACT_LABEL.JURISPRUDENCIA,
  ARTIFACT_LABEL.INTEIRO_TEOR,
  ARTIFACT_LABEL.NAO_IDENTIFICADO,
] as const

const findCurrentJurisTab = (document: Document) =>
  document.decisionTabs?.find((tab) => tab?.linkId === document.docId)?.label || null

const buildJurisArtifactsMetadata = (document: Document) =>
  JURIS_ARTIFACTS.includes(document.artifact as (typeof JURIS_ARTIFACTS)[number])
    ? {
        currentTab: findCurrentJurisTab(document),
        degree: document.degree,
        hasDecisionFactsForReference: Boolean(document.hasDecisionFactsForReference),
        hasLawsuitLink: Boolean(document.decisionLabel && document.lawsuitEntity?.url),
      }
    : {}

const buildExperimentMetadata = (experiments?: ABTestExperiment[]) => {
  const experiment = experiments?.find((exp) => exp.participating) || experiments?.[0]

  const experimentMetadata: Record<string, unknown> = {}
  if (experiment?.experiment) {
    experimentMetadata.experiment = experiment.experiment
    experimentMetadata.participatingAbTest = experiment.participating
    if (experiment?.alternative) {
      experimentMetadata.alternative = experiment.alternative
    }
  }
  return experimentMetadata
}

const getOrigin = (req: GetServerSidePropsContext['req']) => {
  const fullUrl = `https://${req.headers.host}${req.url}`
  try {
    const url = new URL(fullUrl)
    return url.searchParams.get('origin') || undefined
  } catch {
    return undefined
  }
}

export function buildMetricsProperties(
  ctx: Pick<GetServerSidePropsContext, 'query' | 'req' | 'resolvedUrl'>,
  documentData: Document | Topic,
  viewer: Viewer | null,
  experiments?: ABTestExperiment[],
  featureFlags?: FeatureFlag[]
): MetricsProperties {
  const { req } = ctx

  const { headers } = req
  const { 'user-agent': userAgent, referer: referrer } = headers
  const ua = UAParser(userAgent)

  const data: Record<string, unknown> = {
    // device
    browser: ua.browser.name,
    os: ua.os.name,
    user_agent: userAgent,
    dvce_type: ua.device.type || 'desktop',
    dvce_ismobile: ua.device.type === 'mobile',

    app_id: 'web-docview',
    referrer,
    source: referrer,
    origin: getOrigin(req),
    metadata: {
      ...buildExperimentMetadata(experiments),
      experiments: [
        ...(featureFlags?.map((flag) => `${flag.name}:${flag.value}`) || []),
        ...(experiments
          ?.filter(({ participating }) => participating)
          .map((exp) => `${exp.experimentGroup}:${exp.experiment}:${exp.alternative}`) || []),
      ],
    },
    user_id: viewer?.pid ? String(viewer.pid) : null,
  }

  if ('docId' in documentData) {
    data.doc_id = documentData.docId
    data.artifact = documentData.artifact
    data.metadata = {
      ...(data.metadata as Record<string, unknown>),
      ...buildJurisArtifactsMetadata(documentData as Document),
    }
  } else if ('tid' in documentData) {
    data.topic_id = documentData.tid
  }

  for (const key in data) {
    if (data[key] === undefined) {
      delete data[key]
    }
  }

  return data as MetricsProperties
}
